import { ball, body, coverBg, edge, fade, gloss, mix, n, shade, tint, type ArtPal } from './kit';

/** peach (as the llPeach symbol) centred on (0, 0), ~40 px tall */
const PEACH = 'M0 -18C-4 -12 -17 -13 -17 1C-17 12 -9 18 0 18C9 18 17 12 17 1C17 -13 4 -12 0 -18Z';

/**
 * Lucky Lever: an evening lucky-goods shop (the left third stays calm for labels, every focus object sits right of
 * x 213) — a lacquered red-and-gold lever machine with a lit arch, three reel windows (peach · ingot · peach), a gold
 * lever with a red knob on its right, a square lattice window and a shelf with a gourd behind, three paper lanterns on a
 * cord. No casino props (no 7s, cherries, bars, bells, chips or coin piles).
 */
export function luckyLeverCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.red, 0.2) : mix(P.gold, '#FFFFFF', 0.82);
  const mid = P.dark ? mix(P.panel, P.orange, 0.12) : mix(P.orange, '#FFFFFF', 0.86);
  const bottom = P.dark ? mix(P.bg, P.red, 0.1) : mix(P.red, '#FFFFFF', 0.82);
  const bg = coverBg(p, P, top, mid, bottom, [430, 200]);
  /** sunny gold: the light-theme gold reads brown on big shapes */
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.72);
  const red = P.red;
  const wood = shade(mix(P.orange, P.gold, 0.3), P.dark ? 0.55 : 0.4);
  const cream = P.dark ? '#F3E9D2' : '#FFF8E8';
  const pink = mix(P.pink, P.orange, 0.3);
  const ink = P.dark ? tint(P.ink, 0.2) : shade(P.ink, 0.1);
  const ol = (c: string, w = 2) => `stroke="${edge(c, P)}" stroke-width="${w}" stroke-linejoin="round"`;

  const lantern = (x: number, y: number, s: number) =>
    `<g transform="translate(${x} ${y}) scale(${s})">` +
    `<circle cx="0" cy="0" r="50" fill="url(#${p}lh)"/>` +
    `<path d="M0 -60V-30" stroke="${ink}" stroke-opacity=".5" stroke-width="2"/>` +
    `<rect x="-12" y="-31" width="24" height="7" rx="2" fill="url(#${p}gd)" ${ol(gold, 1.4)}/>` +
    `<path d="M-20 -24H20C26 -12 26 12 20 24H-20C-26 12 -26 -12 -20 -24Z" fill="url(#${p}ln)" ${ol(red)}/>` +
    `<path d="M-7 -24C-9 -8 -9 8 -7 24M7 -24C9 -8 9 8 7 24" fill="none" stroke="${gold}" stroke-width="1.6"/>` +
    `<rect x="-12" y="24" width="24" height="7" rx="2" fill="url(#${p}gd)" ${ol(gold, 1.4)}/>` +
    `<path d="M0 31V46M-4 33L-6 46M4 33L6 46" stroke="${gold}" stroke-width="2" stroke-linecap="round"/>` +
    gloss(-9, -10, 4, 10, -6, 0.45) +
    `</g>`;

  const peach = (x: number, y: number) =>
    `<g transform="translate(${x} ${y})"><path d="${PEACH}" fill="url(#${p}pc)" ${ol(pink, 1.6)}/>` +
    `<path d="M0 -16C-3 -4 -3 8 2 16" fill="none" stroke="${edge(pink, P)}" stroke-opacity=".45" stroke-width="1.4"/>` +
    `<path d="M0 -18C-4 -26 -12 -26 -14 -22C-10 -18 -4 -18 0 -18Z" fill="${P.green}" ${ol(P.green, 1.2)}/>` +
    gloss(-8, -2, 4, 3, -40, 0.6) +
    `</g>`;

  const ingot = (x: number, y: number, s: number) =>
    `<g transform="translate(${x} ${y}) scale(${s})">` +
    `<path d="M-20 -6C-12 -4 -6 0 0 0C6 0 12 -4 20 -6C19 -2 17 0 14 1H-14C-17 0 -19 -2 -20 -6Z" fill="url(#${p}gd)" ${ol(gold, 1.4)}/>` +
    `<ellipse cx="0" cy="-2" rx="9" ry="8" fill="url(#${p}gb)" ${ol(gold, 1.4)}/>` +
    `<path d="M-17 0C-10 3 -6 5 0 5C6 5 10 3 17 0C16 10 9 16 0 16C-9 16 -16 10 -17 0Z" fill="url(#${p}gd)" ${ol(gold, 1.6)}/>` +
    gloss(-3, -6, 3, 2, -30, 0.7) +
    `</g>`;

  /** bulbs along the arch (9) and down both sides (5 + 5): lit, warm */
  const bulb = (x: number, y: number) =>
    `<circle cx="${n(x)}" cy="${n(y)}" r="7" fill="url(#${p}bh)"/><circle cx="${n(x)}" cy="${n(y)}" r="3.6" fill="url(#${p}bl)" ${ol(gold, 1)}/>`;
  let bulbs = '';
  for (let i = 0; i < 9; i++) {
    const a = Math.PI + (Math.PI * i) / 8;
    bulbs += bulb(410 + 82 * Math.cos(a), 140 + 66 * Math.sin(a));
  }
  for (let i = 0; i < 5; i++) bulbs += bulb(331, 166 + i * 44) + bulb(489, 166 + i * 44);

  const windows = [peach(366, 200), ingot(410, 200, 1.05), peach(454, 200)];

  const defs =
    `<defs>${bg.defs}` +
    body(`${p}cb`, red, 0.32, 0.42, 0.7, 1) +
    body(`${p}gd`, gold, 0.5, 0.35, 0.5, 1) +
    ball(`${p}gb`, gold, 0.6, 0.3) +
    ball(`${p}ln`, P.dark ? shade(red, 0.05) : tint(red, 0.1), 0.45, 0.35) +
    fade(`${p}lh`, P.orange, P.dark ? 0.45 : 0.3) +
    ball(`${p}kn`, red, 0.55, 0.4) +
    fade(`${p}bh`, P.dark ? '#FFE9A8' : P.orange, P.dark ? 0.7 : 0.4) +
    ball(`${p}bl`, P.dark ? '#FFF4CC' : '#FFE7A3', 0.6, 0.2) +
    ball(`${p}pc`, pink, 0.5, 0.3) +
    body(`${p}gr`, mix(P.gold, P.orange, 0.25), 0.45, 0.4, 0.5, 1) +
    fade(`${p}sh`, P.dark ? '#000000' : shade(P.ink, 0.2), P.dark ? 0.6 : 0.22) +
    `</defs>`;

  /** square lattice window (3 × 3 panes) on the back wall */
  const lattice = (x: number, y: number, w: number) => {
    const t = w / 3;
    return (
      `<rect x="${x}" y="${y}" width="${w}" height="${w}" rx="4" fill="${P.dark ? mix(P.bg, P.gold, 0.12) : '#FFFFFF'}" fill-opacity="${P.dark ? 0.8 : 0.7}" stroke="${wood}" stroke-width="6"/>` +
      `<path d="M${n(x + t)} ${y}V${y + w}M${n(x + 2 * t)} ${y}V${y + w}M${x} ${n(y + t)}H${x + w}M${x} ${n(y + 2 * t)}H${x + w}" stroke="${wood}" stroke-opacity=".75" stroke-width="2.6"/>`
    );
  };

  return (
    defs +
    bg.body +
    lattice(552, 70, 96) +
    // shelf on the right with a gourd and a jade-green jar
    `<rect x="540" y="236" width="100" height="9" fill="${wood}"/>` +
    `<path d="M600 236C590 236 586 228 588 222C590 216 596 216 596 212C592 210 592 202 600 202C608 202 608 210 604 212C604 216 610 216 612 222C614 228 610 236 600 236Z" fill="url(#${p}gr)" stroke="${edge(P.gold, P)}" stroke-width="1.6"/>` +
    `<path d="M567 236V214Q567 208 573 208H581Q587 208 587 214V236Z" fill="${mix(P.green, P.teal, 0.3)}" fill-opacity=".85" stroke="${edge(P.green, P)}" stroke-width="1.6"/>` +
    // floor
    `<path d="M0 372H640V400H0Z" fill="${wood}" fill-opacity="${P.dark ? 0.7 : 0.35}"/>` +
    `<ellipse cx="420" cy="374" rx="130" ry="12" fill="url(#${p}sh)"/>` +
    // cord of lanterns
    `<path d="M228 34Q420 70 640 30" fill="none" stroke="${ink}" stroke-opacity=".45" stroke-width="2"/>` +
    // cabinet: arch + body, gold trim
    `<path d="M320 146Q320 66 410 66Q500 66 500 146V360Q500 372 488 372H332Q320 372 320 360Z" fill="url(#${p}cb)" ${ol(red, 3)}/>` +
    `<path d="M330 146Q330 76 410 76Q490 76 490 146" fill="none" stroke="${gold}" stroke-width="3"/>` +
    ingot(410, 112, 1.1) +
    bulbs +
    // reel window
    `<rect x="338" y="160" width="144" height="82" rx="10" fill="${shade(red, 0.55)}" stroke="url(#${p}gd)" stroke-width="5"/>` +
    [344, 388, 432]
      .map(
        (x) =>
          `<rect x="${x}" y="166" width="44" height="70" rx="5" fill="${cream}" stroke="${shade(cream, 0.25)}" stroke-width="1.2"/>`,
      )
      .join('') +
    windows.join('') +
    `<path d="M340 201H480" stroke="${red}" stroke-opacity=".55" stroke-width="1.6"/>` +
    // lower panel with a gold cloud scroll, the tray
    `<rect x="342" y="258" width="136" height="70" rx="8" fill="${shade(red, 0.2)}" stroke="${gold}" stroke-opacity=".8" stroke-width="2"/>` +
    `<path d="M366 302C366 286 386 284 386 296C386 304 376 304 376 298M454 284C454 300 434 302 434 290C434 282 444 282 444 288M394 296Q410 284 426 296" fill="none" stroke="${gold}" stroke-width="3" stroke-linecap="round"/>` +
    `<rect x="356" y="340" width="108" height="16" rx="6" fill="url(#${p}gd)" ${ol(gold, 1.6)}/>` +
    `<rect x="372" y="345" width="76" height="5" rx="2.5" fill="${shade(gold, 0.55)}"/>` +
    gloss(352, 200, 8, 70, 0, 0.12) +
    gloss(346, 110, 18, 5, -50, 0.35) +
    // lever: hub, arm, red knob
    `<path d="M506 252L546 140" stroke="${edge(gold, P)}" stroke-width="12" stroke-linecap="round"/>` +
    `<path d="M506 252L546 140" stroke="url(#${p}gd)" stroke-width="8" stroke-linecap="round"/>` +
    `<rect x="496" y="236" width="18" height="34" rx="6" fill="url(#${p}gd)" ${ol(gold, 1.8)}/>` +
    `<circle cx="548" cy="132" r="19" fill="url(#${p}kn)" ${ol(red, 2.2)}/>` +
    gloss(541, 124, 6, 4, -35, 0.7) +
    lantern(262, 92, 0.86) +
    lantern(560, 72, 0.6) +
    lantern(612, 60, 0.52)
  );
}
